import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { FilterState, ProjectCategory, ComplexityLevel, ProjectStatus } from "../types";

interface ProjectFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onReset: () => void;
  totalProjects: number;
  filteredCount: number;
}

const ProjectFilters = ({
  filters,
  onFilterChange,
  onReset,
  totalProjects,
  filteredCount,
}: ProjectFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categories: (ProjectCategory | "All")[] = [
    "All",
    "Web Development",
    "Mobile App",
    "Data Science",
    "Machine Learning",
    "Game Development",
    "API Development",
    "DevOps",
    "Academic",
  ];

  const complexityLevels: (ComplexityLevel | "All")[] = [
    "All",
    "Beginner",
    "Intermediate",
    "Advanced",
  ];

  const statusOptions: (ProjectStatus | "All")[] = [
    "All",
    "Completed",
    "In Progress",
    "Maintained",
  ];

  const technologies = [
    "All",
    "React",
    "TypeScript",
    "Node.js",
    "Python",
    "Java",
    "MongoDB",
    "PostgreSQL",
    "Docker",
    "AWS",
    "TensorFlow",
    "React Native",
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, searchQuery: e.target.value });
  };

  const handleCategoryChange = (category: string) => {
    onFilterChange({ ...filters, category: category as ProjectCategory | "All" });
  };

  const handleTechnologyChange = (value: string | number) => {
    onFilterChange({ ...filters, technology: value as string });
  };

  const handleComplexityChange = (value: string | number) => {
    onFilterChange({ ...filters, complexity: value as ComplexityLevel | "All" });
  };

  const handleStatusChange = (value: string | number) => {
    onFilterChange({ ...filters, status: value as ProjectStatus | "All" });
  };

  const activeFiltersCount = [
    filters.category !== "All",
    filters.technology !== "All",
    filters.complexity !== "All",
    filters.status !== "All",
    filters.searchQuery !== "",
  ].filter(Boolean).length;

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Filter" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">Filter Projects</h2>
            <p className="text-sm text-muted-foreground">
              Showing {filteredCount} of {totalProjects} projects
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={onReset}
            >
              Clear ({activeFiltersCount})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden"
          >
            {isExpanded ? "Hide" : "Show"} Filters
          </Button>
        </div>
      </div>

      <div className={`space-y-4 ${isExpanded ? "block" : "hidden lg:block"}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search projects..."
            value={filters.searchQuery}
            onChange={handleSearchChange}
            className="w-full"
          />

          <Select
            placeholder="Select technology"
            options={technologies.map((tech) => ({
              value: tech,
              label: tech,
            }))}
            value={filters.technology}
            onChange={handleTechnologyChange}
          />

          <Select
            placeholder="Select complexity"
            options={complexityLevels.map((level) => ({
              value: level,
              label: level,
            }))}
            value={filters.complexity}
            onChange={handleComplexityChange}
          />

          <Select
            placeholder="Select status"
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
            value={filters.status}
            onChange={handleStatusChange}
          />
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-3">Categories</p>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  filters.category === category
                    ? "bg-primary text-primary-foreground shadow-elevation-1"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectFilters;